import { useState } from 'react';
import { uploadService } from '@/services/api/upload';

interface UseImageUploadOptions {
  tag?: string;
  secure?: boolean;
  maxSize?: number;
  onSuccess?: (url: string) => void;
  onError?: (error: string) => void;
}

export function useImageUpload(options: UseImageUploadOptions = {}) {
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | undefined>();

  const uploadImage = async (file: File): Promise<string | null> => {
    try {
      setIsUploading(true);
      setError(null);

      const imageUrl = await uploadService.uploadFile(file, {
        tag: options.tag,
        secure: options.secure ?? true,
        maxSize: options.maxSize ?? 5 * 1024 * 1024, // Default 5MB
        allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
      });

      // Create a preview URL for the uploaded file
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setPreviewUrl(URL.createObjectURL(file));

      options.onSuccess?.(imageUrl);
      return imageUrl;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Upload failed';
      setError(errorMessage);
      options.onError?.(errorMessage);

      // Reset the file input
      const fileInput = document.getElementById(
        'image-upload'
      ) as HTMLInputElement;
      if (fileInput) {
        fileInput.value = '';
      }
      return null;
    } finally {
      setIsUploading(false);
    }
  };

  const resetImage = () => {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }
    setPreviewUrl(undefined);
    setError(null);
  };

  return {
    uploadImage,
    resetImage,
    isUploading,
    error,
    previewUrl,
  };
}
